/**
 * Patient Display Component
 *
 * Displays patient avatar, name, DOB, and close button
 */

import React from 'react';
import { Avatar } from '@jotpsych/ui';
import { Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, Link, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { Patient } from '@ts/types';
import { formatDob, formatPatientName } from '@utils/patientUtils';

export interface PatientDisplayProps {
  patient: Patient;
  onClose?: () => void;
  /** Callback when patient name is clicked */
  onPatientClick?: (patientId: string) => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientDisplay: React.FC<PatientDisplayProps> = ({
  patient,
  onClose,
  onPatientClick,
  source,
}) => {
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const handleNameClick = () => {
    if (onPatientClick && patient.patient_id) {
      contextBarTracking.patientNameClicked({
        patient_id: patient.patient_id,
        source,
      });
      onPatientClick(patient.patient_id);
    }
  };

  const handleClose = () => {
    if (patient.patient_id) {
      contextBarTracking.patientDeselected({
        patient_id: patient.patient_id,
        source,
      });
    }
    onClose?.();
  };

  return (
    <Box
      data-testid="patient-display"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Avatar
        id={patient.patient_id}
        firstName={patient.first_name}
        lastName={patient.last_name}
        sx={{
          width: 40,
          height: 40,
          fontSize: '1rem',
          fontWeight: 600,
        }}
      />
      <Box>
        {onPatientClick ? (
          <Link
            component="button"
            variant="subtitle1"
            onClick={handleNameClick}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main',
              },
            }}
          >
            {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
          </Link>
        ) : (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {formatPatientName(patient.first_name, patient.last_name, patient.preferred_name)}
          </Typography>
        )}
        {patient.dob && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            DOB: {formatDob(patient.dob)}
          </Typography>
        )}
      </Box>
      {onClose && (
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            ml: 1,
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
